import { Helmet } from 'react-helmet-async';
import { BlogLayout } from '../components/BlogLayout';
import { PageHeader } from '../components/PageHeader';

const aboutImage = 'https://pub-17b41bfc91754e909a0062f5845603e7.r2.dev/about.jpeg';

const pillars = [
  {
    title: 'Chicago roots, global curiosity',
    body: 'Questflow started as long coffee chats in Chicago that turned into a digital newsroom built for the modern reader.'
  },
  {
    title: 'Fictional voices, real rigor',
    body: 'Our writers are crafted personalities, but our standards are grounded in clear sourcing, fast editing, and practical takeaways.'
  },
  {
    title: 'Human flair + workflow speed',
    body: 'We blend human-style storytelling with automated workflows so every piece ships faster without losing character.'
  }
];

const coverage = [
  {
    title: 'Tech & innovation',
    body: 'Emerging platforms, automation, and the tools reshaping how we build.'
  },
  {
    title: 'Business & strategy',
    body: 'Market moves, operator playbooks, and the signals that matter for builders.'
  },
  {
    title: 'Culture & daily living',
    body: 'How tech shows up in work, wellness, homes, and the routines we tweak every day.'
  },
  {
    title: 'Explainers & how-tos',
    body: 'Plain-language guides that break down complex topics into next steps.'
  }
];

const commitments = [
  {
    title: 'Clarity first',
    body: 'We keep intros tight, spell out outcomes, and cut jargon wherever we can.'
  },
  {
    title: 'Transparent sourcing',
    body: 'We note when automation lends a hand, what is pulled from public data, and where human editors intervene.'
  },
  {
    title: 'Always shippable',
    body: 'Templates, checklists, and dotted dividers keep every story consistent and ready to publish.'
  }
];

export default function AboutPage() {
  return (
    <BlogLayout>
      <Helmet>
        <title>About - QuestFlow</title>
        <meta name="description" content="Built like a newsroom. Tuned for automation." />
      </Helmet>
      <div className="page-body about-page">
        <div className="about-top">
          <PageHeader title="About" eyebrow="Questflow" />
        </div>

        <div className="about-lede-row">
          <div className="about-intro-copy">
            <h2 className="about-headline">Built like a newsroom. Tuned for automation.</h2>
            <p className="about-lead">
              Questflow began as a passion project among friends who wanted a digital newspaper powered by curiosity,
              creativity, and smart workflows. What started as shared notes now operates like a full newsroom, where each
              persona-driven writer brings a distinct voice to the stories we publish.
            </p>
          </div>
          <div className="about-badge">
            <div className="tag-pill">Home base: Chicago</div>
            <p className="about-note">We collaborate daily from Chicago while staying plugged into stories everywhere.</p>
          </div>
        </div>

        <div className="about-lede-image">
          <img
            src={aboutImage}
            alt="Questflow team and workspace"
            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
          />
        </div>

        <section className="about-hero">
          <div className="about-hero-copy">
            <div className="eyebrow">Our origin story</div>
            <h2>From coffee chats to a collaborative digital paper</h2>
            <p>
              Questflow blends human-style storytelling with advanced automation to publish faster, smarter, and with more
              personality than a traditional publication. We cover technology, innovation, business, culture, and the
              everyday shifts that come with rapid change.
            </p>
            <div className="about-meta">
              <span className="tag-pill ghost">Editorial voices with distinct styles</span>
              <span className="tag-pill ghost">Automated workflows</span>
              <span className="tag-pill ghost">Reader-first clarity</span>
            </div>
          </div>
          <div className="about-hero-card">
            <h3>How we publish</h3>
            <ul className="about-list">
              <li>
                <span className="about-bullet" aria-hidden="true">
                  &rarr;
                </span>
                <div>
                  <strong>Story-first prompts.</strong> Each piece starts with a clear reader promise and an outline built
                  with editors.
                </div>
              </li>
              <li>
                <span className="about-bullet" aria-hidden="true">
                  &rarr;
                </span>
                <div>
                  <strong>Human + automation editing.</strong> Automated drafts get tightened, fact-checked, and styled for our
                  house tone.
                </div>
              </li>
              <li>
                <span className="about-bullet" aria-hidden="true">
                  &rarr;
                </span>
                <div>
                  <strong>Consistent modules.</strong> Dotted dividers, boxed tags, and meta rows keep every article
                  scannable.
                </div>
              </li>
            </ul>
          </div>
        </section>

        <section className="stack">
          <div className="section-header">
            <h3>What drives us</h3>
            <span className="inline-link">Reader outcomes over pageviews.</span>
          </div>
          <div className="about-grid">
            {pillars.map((pillar) => (
              <article className="card compact about-card" key={pillar.title}>
                <div className="card-body">
                  <h4 className="title">{pillar.title}</h4>
                  <p className="excerpt">{pillar.body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="stack">
          <div className="section-header">
            <h3>What we cover</h3>
            <span className="inline-link">From deep dives to quick hits.</span>
          </div>
          <div className="about-grid">
            {coverage.map((item) => (
              <article className="card compact about-card" key={item.title}>
                <div className="card-body">
                  <h4 className="title">{item.title}</h4>
                  <p className="excerpt">{item.body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="stack">
          <div className="section-header">
            <h3>How we keep trust</h3>
            <span className="inline-link">Thoughtfully curated, editor-approved.</span>
          </div>
          <div className="about-grid">
            {commitments.map((item) => (
              <article className="card compact about-card" key={item.title}>
                <div className="card-body">
                  <h4 className="title">{item.title}</h4>
                  <p className="excerpt">{item.body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </BlogLayout>
  );
}
